import CoreBase from '../../services/engines/CoreBase';
import ComponentNotifier from '../../services/wss/ComponentNotifier';
import calEventFactory from '../Types/CalEventFactory';

const _name = 'CalEventEditEngine';

/**
 * Tracks which CalEvent (by rk) is currently being edited in EditEventDialog.
 * Only one event can be in edit mode at a time — CalendarCard reads editingRk
 * to grey out the edit pen on other cards until save or cancel.
 *
 * Singleton — one instance shared across the app. Pub/sub via ComponentNotifier
 * so only subscribed components (EditEventDialog, CalendarCard) repaint on change —
 * not the entire CalendarWidget tree.
 */
class CalEventEditEngine extends CoreBase {
    _name = _name;
    _editingRk = null;
    _newEventDraft = null;

    /**
     * Starts editing an event by rk, or '__new__' for a fresh draft.
     * Creates the draft object once — stays stable until endEdit.
     * @param {string} rk
     */
    startEdit = (rk) => {
        try {
            if (rk === '__new__') this._newEventDraft = calEventFactory.createNew();
            this._editingRk = rk;
            this._notify();
        } catch (error) {
            this.logError('startEdit', error.message);
        }
    };

    /**
     * Ends the current edit session — clears editingRk and the new-event draft.
     */
    endEdit = () => {
        try {
            this._editingRk = null;
            this._newEventDraft = null;
            this._notify();
        } catch (error) {
            this.logError('endEdit', error.message);
        }
    };

    /**
     * Patches fields on the current new-event draft.
     * Call after startEdit('__new__') to pre-fill fields from e.g. a slash command.
     * No-op if no draft is active.
     * @param {{ label?: string, date?: string, time?: string, cat?: string, desc?: string, remind?: string }} fields
     */
    patchDraft = (fields) => {
        if (!this._newEventDraft) return;
        Object.assign(this._newEventDraft, fields);
        this._notify();
    };

    /**
     * Returns the rk of the event currently being edited, '__new__', or null.
     * @returns {string|null}
     */
    getEditingRk = () => {
        return this._editingRk;
    };

    /**
     * Returns the draft CalEventObj for a new event, or null when not editing a new event.
     * @returns {CalEventObj|null}
     */
    getNewEventDraft = () => {
        return this._newEventDraft;
    };

    // ─── private ──────────────────────────────────────────

    _notify = () => {
        ComponentNotifier.notify('editChanged', _name, {
            editingRk: this._editingRk,
            newEventDraft: this._newEventDraft,
        });
    };
}

const calEventEditEngine = new CalEventEditEngine();
export default calEventEditEngine;
